<template>
  <div class="px-4 py-6 sm:px-0">
    <!-- Sync Progress Modals -->
    <SyncProgressModal
      :is-open="isImportModalOpen"
      sync-type="prospects-historical"
      :progress="importProgress"
      :has-error="importHasError"
      :error-message="importErrorMessage"
      @close="closeImportModal"
      @retry="retryImport"
    />
    <SyncProgressModal
      :is-open="isTrainModalOpen"
      sync-type="ml-training"
      :progress="trainProgress"
      :has-error="trainHasError"
      :error-message="trainErrorMessage"
      @close="closeTrainModal"
      @retry="retryTrain"
    />
    <SyncProgressModal
      :is-open="isTimelineModalOpen"
      sync-type="timeline-historical"
      :progress="timelineProgress"
      :has-error="timelineHasError"
      :error-message="timelineErrorMessage"
      @close="closeTimelineModal"
      @retry="retryTimeline"
    />

    <!-- Sync Results -->
    <SyncResultNotification :result="importSyncResult" @close="importSyncResult = null" />
    <SyncResultNotification :result="trainSyncResult" @close="trainSyncResult = null" />
    <SyncResultNotification :result="timelineSyncResult" @close="timelineSyncResult = null" />

    <div class="mt-6">
      <PageTabs v-model="activeTab" :tabs="tabs" :sticky="false">
        <template v-if="isDevMode" #controls>
          <div class="relative">
            <span
              v-if="importSyncLabel"
              :title="importSyncFull"
              class="absolute bottom-full right-0 mb-0.5 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap"
            >
              {{ importSyncLabel }}
            </span>
            <button
              @click="startImport"
              :disabled="isImportSyncing || isTrainSyncing || isTimelineSyncing"
              class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowPathIcon class="h-4 w-4" :class="{ 'animate-spin': isImportSyncing }" />
              <span v-if="isImportSyncing">Importeren...</span>
              <span v-else class="hidden sm:inline">Saleskansen importeren</span>
            </button>
          </div>
          <div class="relative">
            <span
              v-if="trainSyncLabel"
              :title="trainSyncFull"
              class="absolute bottom-full right-0 mb-0.5 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap"
            >
              {{ trainSyncLabel }}
            </span>
            <button
              @click="startTrain"
              :disabled="isImportSyncing || isTrainSyncing || isTimelineSyncing"
              class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <CpuChipIcon class="h-4 w-4" :class="{ 'animate-pulse': isTrainSyncing }" />
              <span v-if="isTrainSyncing">Trainen...</span>
              <span v-else class="hidden sm:inline">Model trainen</span>
            </button>
          </div>
          <div class="relative">
            <span
              v-if="timelineSyncLabel"
              :title="timelineSyncFull"
              class="absolute bottom-full right-0 mb-0.5 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap"
            >
              {{ timelineSyncLabel }}
            </span>
            <button
              @click="startTimeline"
              :disabled="isImportSyncing || isTrainSyncing || isTimelineSyncing"
              class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ClockIcon class="h-4 w-4" :class="{ 'animate-spin': isTimelineSyncing }" />
              <span v-if="isTimelineSyncing">Verrijken...</span>
              <span v-else class="hidden sm:inline">Timeline verrijken</span>
            </button>
          </div>
        </template>
      </PageTabs>
    </div>

    <HoursRevenueView v-if="activeTab === 'uren-omzet'" :reload-trigger="reloadTrigger" />
    <WorkforceForecastView
      v-if="activeTab === 'workforce-forecast'"
      :reload-trigger="reloadTrigger"
    />
    <ArchiveView v-if="activeTab === 'archief'" :reload-trigger="reloadTrigger" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { ArrowPathIcon, CpuChipIcon, ClockIcon } from '@heroicons/vue/24/outline'
import { predictionService } from '@/services/predictionService.js'
import { prospectsLeadsService } from '@/services/prospectService.js'
import { useSyncWithProgress } from '@/composables/data/useSyncWithProgress.js'
import { SYNC_COMPLETED_EVENT } from '@/composables/data/useSyncRefresh.js'
import { useLastSynced } from '@/composables/data/useLastSynced'
import { useAuth } from '@/composables/auth/useAuth'
import HoursRevenueView from '@/components/dashboard/HoursRevenueView.vue'
import WorkforceForecastView from '@/components/dashboard/WorkforceForecastView.vue'
import ArchiveView from '@/components/dashboard/ArchiveView.vue'
import PageTabs from '@/components/ui/PageTabs.vue'
import SyncProgressModal from '@/components/common/modals/SyncProgressModal.vue'
import SyncResultNotification from '@/components/common/feedback/SyncResultNotification.vue'

const isDevMode = import.meta.env.VITE_SKIP_AUTH === 'true'
const { isAdmin, hasDashboardAccess } = useAuth()

const activeTab = ref('uren-omzet')
const tabs = computed(() => {
  const base = [
    {
      key: 'uren-omzet',
      label: 'Realisatie en Prognose',
      description:
        'Bekijk de gerealiseerde en verwachte uren en omzet per maand en team voor de komende periode.'
    }
  ]
  if (hasDashboardAccess.value) {
    base.push({
      key: 'workforce-forecast',
      label: 'To hire or not to hire',
      description:
        'Vergelijk de verwachte capaciteit met de vraag per team en functieniveau en zie waar tekorten ontstaan.'
    })
  }
  if (isAdmin.value) {
    base.push({
      key: 'archief',
      label: 'Archief',
      description:
        'Bekijk afgeronde en gearchiveerde projecten en hun eerdere bezetting en resultaten.'
    })
  }
  return base
})

// If dashboard access is revoked while that tab is open, fall back to the regular
// dashboard instead of leaving the user on a tab they can no longer use.
watch(
  () => hasDashboardAccess.value,
  () => {
    if (!tabs.value.some(tab => tab.key === activeTab.value)) {
      activeTab.value = 'uren-omzet'
    }
  }
)

const reloadTrigger = ref(0)

const handleSyncCompleted = () => {
  reloadTrigger.value++
}

onMounted(() => window.addEventListener(SYNC_COMPLETED_EVENT, handleSyncCompleted))
onUnmounted(() => window.removeEventListener(SYNC_COMPLETED_EVENT, handleSyncCompleted))

const {
  isModalOpen: isImportModalOpen,
  isSyncing: isImportSyncing,
  progress: importProgress,
  hasError: importHasError,
  errorMessage: importErrorMessage,
  syncResult: importSyncResult,
  startSync: startImport,
  retrySync: retryImport,
  closeModal: closeImportModal
} = useSyncWithProgress('prospects-historical', prospectsLeadsService.syncHistorical)

const {
  isModalOpen: isTrainModalOpen,
  isSyncing: isTrainSyncing,
  progress: trainProgress,
  hasError: trainHasError,
  errorMessage: trainErrorMessage,
  syncResult: trainSyncResult,
  startSync: startTrain,
  retrySync: retryTrain,
  closeModal: closeTrainModal
} = useSyncWithProgress('ml-training', predictionService.trainModel)

const {
  isModalOpen: isTimelineModalOpen,
  isSyncing: isTimelineSyncing,
  progress: timelineProgress,
  hasError: timelineHasError,
  errorMessage: timelineErrorMessage,
  syncResult: timelineSyncResult,
  startSync: startTimeline,
  retrySync: retryTimeline,
  closeModal: closeTimelineModal
} = useSyncWithProgress('timeline-historical', prospectsLeadsService.syncTimelineHistorical)

const { lastSyncedLabel: importSyncLabel, lastSyncedFull: importSyncFull } =
  useLastSynced('prospects-historical')
const { lastSyncedLabel: trainSyncLabel, lastSyncedFull: trainSyncFull } =
  useLastSynced('ml-training')
const { lastSyncedLabel: timelineSyncLabel, lastSyncedFull: timelineSyncFull } =
  useLastSynced('timeline-historical')
</script>
